/**
 * Agency Balance Management API
 *
 * Uses existing endpoints where available:
 * - addChildAgencyBalance for prepaid top-up (positive/negative amounts)
 * - requestChildAgencies for agency listing
 *
 * New endpoints required from backend:
 * - GET  .../ssp/child-agency/balances/         — list with balance details
 * - GET  .../ssp/child-agency/{id}/balance-history/ — change log
 */

import { request } from './request';
import { getPrefix, requestChildAgencies, requestChildAgency } from './agency';


/**
 * Fetch all child agencies with balance information
 *
 * Uses /ssp/child-agency/{id}/ to get details including balance and credit_balance.
 *
 * @returns {Promise<Array>} agencies with balance data
 */
export async function requestAgenciesBalance() {
	const response = await requestChildAgencies();
	const agenciesList = response.data || response.results || response || [];

	const agencies = await Promise.all(
		agenciesList.map(async (agency) => {
			try {
				const details = await requestChildAgency(agency.id);
				return {
					id: agency.id,
					name: agency.name,
					currency: details.currency || '—',
					balance: details.balance != null ? Number(details.balance) : 0,
					prepaid_spent: details.prepaid_spent != null ? Number(details.prepaid_spent) : 0,
					credit: details.credit_balance != null ? Number(details.credit_balance) : 0,
					credit_spent: details.credit_spent != null ? Number(details.credit_spent) : 0,
					main_account_number: details.main_account_number || null,
					credit_account_number: details.credit_account_number || null,
				};
			} catch (err) {
				return {
					id: agency.id,
					name: agency.name,
					currency: agency.currency || '—',
					balance: 0,
					prepaid_spent: 0,
					credit: 0,
					credit_spent: 0,
					main_account_number: null,
					credit_account_number: null,
				};
			}
		})
	);

	return agencies;
}



/**
 * Fetch balance change history for a specific child agency
 *
 * Endpoint: GET /api/v1/agency/{agency_id}/ssp/child-agency/{child_id}/balance-history/
 * Query params: ?date_from=YYYY-MM-DD&date_to=YYYY-MM-DD
 * Expected response: [{ field, old_value, new_value, user, changed_at }, ...]
 *
 * @param {number} agencyId - child agency ID
 * @param {object} [params] - optional query params { date_from, date_to }
 * @returns {Promise<Array>}
 */
export async function requestBalanceChangeHistory(agencyId, params) {
	return await request(`${getPrefix()}ssp/child-agency/${agencyId}/balance-history/`, params);
}
